<template>
  <div id="graph-view">
    <div id="graph-container">

      <div id="graph"></div>
      <div class="zoom-button">
        <div class="div" @click="e => zoom(true)"><i class="fas fa-plus"/></div>
        <div class="div" @click="e => zoom(false)"><i class="fas fa-minus"/></div>
      </div>
      <DataViewerContainer />
    </div>
  </div>
</template>
<script lang="ts">
import {defineComponent, ref, onMounted, inject, watchEffect, watch, reactive} from "vue"
import {Network, Options} from "vis-network/peer";
import {DataSet} from "vis-data"
import Search from "@/components/Search.vue";
import {useMapStore} from "@/mixins/mapStore";
import DataViewerContainer from "@/components/DataViewerContainer.vue";

export default defineComponent({
  name: "GraphView",
  components: {DataViewerContainer, Search},
  emits: {
    "mode:tabular": () => true,
  },
  setup() {
    const mapStore = useMapStore();
    const multiLineLabel = (title: string) => {
      let i = 0;
      return title.replace(new RegExp(' ', 'g'), c => {
        i++
        return i % 2 && i != 0 ? c : '\n'
      });
    }
    const lineBreakYears = (title: string) => {
      let i = 0;
      return title.replace(new RegExp(',', 'g'), c => {
        i++
        return i % 5 && i != 0 ? c : ',\n'
      });
    }
    const visOptions: Options = {
      physics: {
        enabled: false,
      },
      layout: {
        hierarchical: {
          enabled: true,
          nodeSpacing: 340,
          levelSeparation: 200,
        }
      },
      nodes: {
        margin: {top: 15, right: 15, bottom: 15, left: 15},
        font: {
          face: "Helvetica",
        },
        shape: "box"
      },
      edges: {
        width: 1,
        selectionWidth: 1,
        smooth: false,
      },
      interaction: {
        dragNodes: false
      }
    }
    const visData = {
      nodes: new DataSet([]),
      edges: new DataSet([])
    }

    let network: Network;

    const nodeStyle = {
      root: {
        color: "#ffa832",
        font: {
          color: 'white',
          size: 20,
          multi: 'html',
          bold: {
            size: 28
          }
        }
      },
      node: {
        color: "#3c474d",
        bold: true,
        font: {color: 'white', size: 20},
      },
      topic: {
        color: "#ffa832",
        bold: true,
        font: {color: 'white', size: 20},
      },
      livingMa: {
        color: "#ffa832",
        font: {
          color: 'white',
          size: 16,
          multi: 'html',
          bold: {
            size: 20
          }
        }
      },
      studyType: {
        color: "#229b00",
        font: {
          color: 'white',
          size: 16,
          multi: 'html',
          bold: {
            size: 20
          }
        }
      },
      studyTypeNotExist: {
        color: "#c50018",
        font: {
          color: 'white',
          size: 16,
          multi: 'html',
          bold: {
            size: 20
          }
        }
      },
      defGuide: {
        color: {
          background: "white",
         color: "#FF0000",
        },
        font: {color: '#3c474d', size: 20},
      },
      study: {
        color: {
          background: "white",
          border: "#229b00",
        },
        font: {color: '#3c474d', size: 20},
      }
    }
    const updateVisData = () => {
      const localVisData = {
        nodes: new DataSet([]),
        edges: new DataSet([])
      }
      localVisData.nodes.add({
        id: "n0",
        label: `<b>${mapStore.state.map.title} </b> \n ${mapStore.state.map.date ? mapStore.state.map.date : ""}`,
        color: nodeStyle.root.color,
        level: 0,
        shape: "box",
        margin: 20,
        shapeProperties: {borderRadius: 0},
        font: nodeStyle.root.font
      })
      mapStore.getNodesByParent(0).forEach(node => {
        localVisData.nodes.add({
          id: `n${node.id}`,
          label: multiLineLabel(node.title),
          color: nodeStyle.node.color,
          font: nodeStyle.node.font,
          level: 1,
        })
        localVisData.edges.add({id: `en0n${node.id}`, from: "n0", to: `n${node.id}`})
      })
      localVisData.nodes.add({
        id: `dg`,
        label: multiLineLabel("Definition and Guidelines"),
        color: nodeStyle.node.color,
        font: nodeStyle.node.font,
        level: 1,
      })
      localVisData.edges.add({id: `en0dg`, from: "n0", to: `dg`})
      if(mapStore.state.defGuide){

        localVisData.nodes.add({
          id: `d0`,
          label: multiLineLabel("Definition"),
          color: nodeStyle.node.color,
          font: nodeStyle.node.font,
          level: 2,
        })
        localVisData.edges.add({id: `edgd0`, from: "dg", to: `d0`})
        localVisData.nodes.add({
          id: `g0`,
          label: multiLineLabel("Guidelines"),
          color: nodeStyle.node.color,
          font: nodeStyle.node.font,
          level: 2,
        })
        localVisData.edges.add({id: `edgg0`, from: "dg", to: `g0`})
        if(mapStore.state.defOpen){
          mapStore.state.map.definitionsAndGuidelines.definitions.forEach(d => {
            localVisData.nodes.add({
              id: `d${d.id}`,
              label: multiLineLabel(d.titleShown),
              color: nodeStyle.defGuide.color,
              font: nodeStyle.defGuide.font,
              level: 3,
            })
            localVisData.edges.add({id: `ed0d${d.id}`, from: "d0", to: `d${d.id}`})
          })
        }
        if(mapStore.state.guideOpen){
          mapStore.state.map.definitionsAndGuidelines.guidelines.forEach(g => {
            localVisData.nodes.add({
              id: `g${g.id}`,
              label: multiLineLabel(g.titleShown),
              color: nodeStyle.defGuide.color,
              font: nodeStyle.defGuide.font,
              level: 3,
            })
            localVisData.edges.add({id: `eg0g${g.id}`, from: "g0", to: `g${g.id}`})
          })
        }
      }
      mapStore.state.nodePath.forEach(parent => {
        const parentId = `n${parent.id}`;
        const level = mapStore.getLevel(parent, 1);
        // render Nodes
        if (parent.topic) {

          const livingMaId = `l${parentId}`
          localVisData.nodes.add({
            id: livingMaId,
            label: "Living \n Meta-Analysis",
            color: nodeStyle.topic.color,
            font: nodeStyle.topic.font,
            level: level + 2
          })
          localVisData.edges.add({id: `e${parentId}${livingMaId}`, from: parentId, to: livingMaId})

          if (parent.topic.rct?.length > 0) {
            const rctId = `t${parentId}r`
            localVisData.nodes.add({
              id: rctId,
              label: `<b>${parent.topic.rct.length} RCT </b> \n ${lineBreakYears(parent.topic.rctYears)}`,
              color: nodeStyle.studyType.color,
              font: nodeStyle.studyType.font,
              level: level + 2
            })
            localVisData.edges.add({id: `e${parentId}${rctId}`, from: parentId, to: rctId})
            if (mapStore.state.openStudyTypes.has(rctId)) {
              parent.topic.rct.forEach(study => {
                const studyId = `r${study.id}${parentId}`
                localVisData.nodes.add({
                  id: studyId,
                  label: `${study.firstAuthor} ${study.year}`,
                  color: nodeStyle.study.color,
                  font: nodeStyle.study.font,
                  level: level + 3
                })
                localVisData.edges.add({id: `e${rctId}${studyId}`, from: rctId, to: studyId})
              })
            }
          } else {

            const rctId = `t${parentId}re`
            localVisData.nodes.add({
              id: rctId,
              label: `<b>${parent.topic.rct.length} RCT </b> `,
              color: nodeStyle.studyTypeNotExist.color,
              font: nodeStyle.studyTypeNotExist.font,
              level: level + 2
            })
            localVisData.edges.add({id: `e${parentId}${rctId}`, from: parentId, to: rctId})
          }
          if (parent.topic.sr?.length > 0) {
            const srId = `t${parentId}s`
            localVisData.nodes.add({
              id: srId,
              label: `<b>${parent.topic.sr.length} SR </b>\n ${lineBreakYears(parent.topic.srYears)}`,
              color: nodeStyle.studyType.color,
              font: nodeStyle.studyType.font,
              level: level + 2,
            })
            localVisData.edges.add({id: `e${parentId}${srId}`, from: parentId, to: srId})
            if (mapStore.state.openStudyTypes.has(srId)) {
              parent.topic.sr.forEach(study => {
                const studyId = `s${study.id}${parentId}`
                localVisData.nodes.add({
                  id: studyId,
                  label: `${study.firstAuthor} ${study.year}`,
                  color: nodeStyle.study.color,
                  font: nodeStyle.study.font,
                  level: level + 3
                })
                localVisData.edges.add({id: `e${srId}${studyId}`, from: srId, to: studyId})
              })
            }
          } else {
            const srId = `t${parentId}se`
            localVisData.nodes.add({
              id: srId,
              label: `<b>${parent.topic.sr.length} SR</b> `,
              color: nodeStyle.studyTypeNotExist.color,
              font: nodeStyle.studyTypeNotExist.font,
              level: level + 2
            })
            localVisData.edges.add({id: `e${parentId}${srId}`, from: parentId, to: srId})
          }

          if (parent.topic.prot?.length > 0) {
            const protId = `t${parentId}p`
            localVisData.nodes.add({
              id: protId,
              label: `${parent.topic.prot.length} Ongoing`,
              color: nodeStyle.topic.color,
              font: nodeStyle.topic.font,
              level: level + 2
            })
            localVisData.edges.add({id: `e${parentId}${protId}`, from: parentId, to: protId})

            if (mapStore.state.openStudyTypes.has(protId)) {
              parent.topic.prot.forEach(study => {
                const studyId = `p${study.id}${parentId}`
                localVisData.nodes.add({
                  id: studyId,
                  label: multiLineLabel(study.identifier.value),
                  color: nodeStyle.study.color,
                  font: nodeStyle.study.font,
                  level: level + 3
                })
                localVisData.edges.add({id: `e${protId}${studyId}`, from: protId, to: studyId})
              })
            }
          } else {

            const protId = `t${parentId}pe`
            localVisData.nodes.add({
              id: protId,
              label: `<b>${parent.topic.prot.length} Ongoing</b> `,
              color: nodeStyle.studyTypeNotExist.color,
              font: nodeStyle.studyTypeNotExist.font,
              level: level + 2
            })
            localVisData.edges.add({id: `e${parentId}${protId}`, from: parentId, to: protId})
          }
        } else {

          mapStore.getNodesByParent(parent.id).forEach(
              node => {
                const nodeId = `n${node.id}`
                const edgeId = `e${parentId}${nodeId}`;
                if (node.topic) {
                  localVisData.nodes.add({
                    id: nodeId,
                    label: multiLineLabel(node.topic.title),
                    color: nodeStyle.topic.color,
                    font: nodeStyle.topic.font,
                    level: level + 1
                  })
                  localVisData.edges.add({id: edgeId, from: parentId, to: nodeId, color: nodeStyle.topic.color})
                } else {
                  localVisData.nodes.add({
                    id: nodeId,
                    label: multiLineLabel(node.title),
                    color: nodeStyle.node.color,
                    font: nodeStyle.node.font,
                    level: level + 1,
                  })
                  localVisData.edges.add({id: edgeId, from: parentId, to: nodeId})
                }
              }
          )
        }
        // Render Parents
      })

      //Sync changes
      //Remove
      const ids = localVisData.nodes.getIds()
      visData.nodes.forEach(((item, id) => {
        if (ids.findIndex(x => x === id) == -1) {
          visData.nodes.remove(id)
          visData.edges.getIds().filter(x => String(x).includes(String(id))).forEach(e => visData.edges.remove(e))
        }
      }))
      //Add
      localVisData.nodes.forEach(((item, id) => {
        if (visData.nodes.getIds().findIndex(x => x === id) == -1) {
          visData.nodes.add(item);
          localVisData.edges.getIds().filter(x => String(x).includes(String(id))).forEach(e => {
            if (visData.edges.getIds().findIndex(x => x === e) == -1) {
              visData.edges.add(localVisData.edges.get(e))
            }
          })
        }
      }))


    }
    window.addEventListener("redraw", () => updateVisData());
    updateVisData()
    onMounted(() => {
      network = new Network(document.getElementById("graph"), visData, visOptions)
      network.on("click", (e) => {
        if (e.nodes.length == 1) {
          const id: string = e.nodes[0];
          if (id === "n0") {
            return;
          }
          if (id === "dg"){
            mapStore.goToDefGuide()
            updateVisData()
            return;
          }
          switch (id.charAt(0)) {
            case 'l':
              const nodeId = Number(id.substring(2))
              const node = mapStore.getNodeById(nodeId)
              window.open(node.topic.livingMaPath, '_blank');
              break

            case 'n':
              mapStore.addToNodePath(mapStore.getNodeById(Number(id.substring(1))));
              break;
            case 't':
              if (id.charAt(id.length - 1) !== "e") {
                mapStore.addToOpenStudyTypes(id);
              }

              break;
            case 's':
              break
            case 'g':
              const l_Id =Number(id.substring(1));
              if(l_Id){
                mapStore.onGuideClick(mapStore.getGuideById(l_Id));
              } else {
                mapStore.onGuideOpen()
              }
              break
            case 'd':
              const l_dId =Number(id.substring(1));
              if(l_dId){
                mapStore.onDefClick(mapStore.getDefById(l_dId));
              } else {
                mapStore.onDefOpen()
              }
              break

          }
          updateVisData()
          network.redraw()

        }
      })
      network.on("select", (e) => {
        if (e.nodes.length == 1) {
          const id: string = e.nodes[0];
          switch (id.charAt(0)) {
            case 'n':
              mapStore.clearStudy()
              break;
            case 't':
              mapStore.clearStudy()
              break;
            case 'r':
              mapStore.clearStudy()
              mapStore.state.rct = mapStore.getRctById(Number(id.substring(1).split("n")[0]))
              break
            case 's':
              mapStore.clearStudy()
              mapStore.state.sr = mapStore.getSrById(Number(id.substring(1).split("n")[0]))
              break
            case 'p':
              mapStore.clearStudy()
              mapStore.state.prot = mapStore.getProtById(Number(id.substring(1).split("n")[0]))
              break
          }
        }
      })

    })

    const zoom = (a_In: boolean) => {
      if (a_In) {
        network.moveTo({scale: network.getScale() + 0.2, animation: true})
      } else {
        network.moveTo({scale: network.getScale() - 0.2, animation: true})
      }
    }
    return {
      mapStore,
      zoom,
    }

  }
})
</script>
<style lang="scss">
@import "src/assets/styles/vars";

#graph-view {
  width: 100%;
  height: 95vh;
  flex-grow: 1;
  position: relative;

  .zoom-button {
    position: fixed;
    top: 7vh;
    left: $default-padding;

    div {
      width: 4vh;
      height: 4vh;
      cursor: pointer;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: $primary;

        i {
          color: white;
        }

      }
    }
  }

  #graph-container {
    display: flex;
    height: 100%;
    flex-direction: row;

    #graph {
      .vis-network:focus {
        outline: none;
      }

      flex-grow: 1;
      height: 100%;
    }
  }

}

</style>